import { ITour } from './i-tour';
import {
  getBuiltInButtons,
  preparations,
  TourDeviceType,
} from './default-step-options';
import shepherd from 'shepherd.js';
import { TranslateService } from '@ngx-translate/core';

export const analyticsSteps = (
  deviceType: TourDeviceType,
  translate: TranslateService
): shepherd.Step.StepOptions[] => {
  const onlyVerticalAlignment = deviceType.deviceType === 'mobile';
  const platformRequiresToOpenSideNav =
    deviceType.deviceType === 'mobile' || deviceType.deviceType === 'tablet';
  const platformRequiresToOpenCardActionsModal =
    deviceType.deviceType === 'mobile';
  const focusButtonInTheCardHeader = deviceType.deviceType !== 'mobile';
  const t = (key: string) => translate.instant(key);
  const buttons = getBuiltInButtons(translate);
  const _preparations = (x: {
    shouldOpenCardsMenu: boolean;
    shouldOpenSideNav: boolean;
    requiresScrollTop?: true;
  }) =>
    preparations({
      shouldOpenCardsMenu: x.shouldOpenCardsMenu,
      shouldOpenSideNav: x.shouldOpenSideNav,
      platformRequiresToOpenSideNav,
      platformRequiresToOpenCardActionsModal,
    }).then(async () => {
      if ('requiresScrollTop' in x) {
        const cardsScrollContainer = document.querySelector('.cards-wrapper');
        cardsScrollContainer.scrollTop = 0;
        await new Promise((resolve) => setTimeout(resolve, 300)); // 250ms is header animation
      }
    });

  const addToCollectionSteps: shepherd.Step.StepOptions[] = platformRequiresToOpenCardActionsModal
    ? [
        {
          attachTo: {
            element: '.show-mobile-menu-btn',
            on: onlyVerticalAlignment ? 'bottom-end' : 'right-end',
          },
          beforeShowPromise: () =>
            _preparations({
              shouldOpenCardsMenu: false,
              shouldOpenSideNav: false,
              requiresScrollTop: true,
            }),
          scrollTo: true,
          buttons: [buttons.back, buttons.next],
          classes: 'step-add-card',
          highlightClass: 'hover-effect-icon-btn',
          id: 'addToCollection',
          title: t('onboarding.shared.cardsMenuTitle'),
          text: t('onboarding.shared.cardsMenuText'),
        } as shepherd.Step.StepOptions,
        {
          attachTo: {
            element: 'vantage-platform-add-to-collection.mobile>button',
            on: onlyVerticalAlignment ? 'top-end' : 'right-end',
          },
          beforeShowPromise: () =>
            _preparations({
              shouldOpenCardsMenu: true,
              shouldOpenSideNav: false,
            }),
          buttons: [buttons.back, buttons.next],
          classes: 'step-add-card',
          highlightClass: 'hover-effect-icon-btn',
          id: 'addToCollection',
          title: t('onboarding.shared.addCardTitle'),
          text: t('onboarding.shared.addCardText'),
        } as shepherd.Step.StepOptions,
    ]
    : [
        {
          attachTo: {
            element: 'vantage-platform-add-to-collection>button',
            on: onlyVerticalAlignment ? 'bottom-end' : 'right-end',
          },
          beforeShowPromise: () =>
            _preparations({
              shouldOpenCardsMenu: false,
              shouldOpenSideNav: false,
            }),
          buttons: [buttons.back, buttons.next],
          classes: 'step-add-card',
          highlightClass: 'hover-effect-icon-btn',
          id: 'addToCollection',
          title: t('onboarding.shared.addCardTitle'),
          text: t('onboarding.shared.addCardText'),
        } as shepherd.Step.StepOptions,
    ];

  return [
    {
      attachTo: {
        element: '.tile-text',
        on: 'top-start',
      },
      beforeShowPromise: () =>
        _preparations({
          shouldOpenSideNav: false,
          shouldOpenCardsMenu: false,
          requiresScrollTop: true,
        }),
      scrollTo: false,
      popperOptions: {
        modal: false,
      },
      buttons: [buttons.next],
      classes: 'step-insight-cards',
      id: 'insightCards',
      title: t('onboarding.analytics.steps.insightCards.title'),
      text: t('onboarding.analytics.steps.insightCards.text'),
    } as shepherd.Step.StepOptions,
    {
      attachTo: {
        element:
          'div.ps-content > nav:nth-child(1) > ul.bottom-line-devider+ul > li:nth-child(1)',
        on: onlyVerticalAlignment ? 'bottom-end' : 'left-start',
      },
      beforeShowPromise: () =>
        _preparations({ shouldOpenSideNav: true, shouldOpenCardsMenu: false }),
      buttons: [buttons.back, buttons.next],
      classes: 'step-focus-areas',
      id: 'focusAreas',
      title: t('onboarding.analytics.steps.visualisations.title'),
      text: t('onboarding.analytics.steps.visualisations.text'),
    } as shepherd.Step.StepOptions,
    {
      attachTo: {
        element: focusButtonInTheCardHeader
          ? 'vantage-platform-question > div.focus-button-container > button'
          : '.focus-button-block>.focus-button',
        on: onlyVerticalAlignment ? 'bottom-end' : 'right-start',
      },
      scrollTo: true,
      beforeShowPromise: () =>
        _preparations({ shouldOpenSideNav: false, shouldOpenCardsMenu: false }),
      buttons: [buttons.back, buttons.next],
      highlightClass: 'visible',
      classes: 'step-focus-card',
      id: 'focusCard',
      title: t('onboarding.analytics.steps.viewReport.title'),
      text: t('onboarding.analytics.steps.viewReport.text'),
    } as shepherd.Step.StepOptions,
    {
      attachTo: {
        element: '.static-content-wrapper .nav-item.create-collection',
        on: 'top-start',
      },
      beforeShowPromise: () =>
        _preparations({ shouldOpenSideNav: true, shouldOpenCardsMenu: false }),
      scrollTo: true,
      buttons: [buttons.back, buttons.next],
      classes: 'step-create-collection',
      highlightClass: 'hover-effect-btn',
      id: 'createCollection',
      title: t('onboarding.shared.createCollectionTitle'),
      text: t('onboarding.shared.createCollectionText'),
    } as shepherd.Step.StepOptions,
    ...addToCollectionSteps,
    {
      attachTo: {
        element: platformRequiresToOpenCardActionsModal
          ? 'button.bottom-sheet-menu-item.card-share-2-teams'
          : 'button.card-share-2-teams',
        on: 'bottom-end',
      },
      beforeShowPromise: () =>
        _preparations({
          shouldOpenCardsMenu: true,
          shouldOpenSideNav: false,
        }),
      buttons: [buttons.back, buttons.next],
      classes: 'step-share-card',
      highlightClass: 'hover-effect-icon-btn',
      id: 'shareCard',
      title: t('onboarding.shared.shareCardTitle'),
      text: t('onboarding.shared.shareCardText'),
    } as shepherd.Step.StepOptions,
    {
      attachTo: {
        element:
          'vp-side-nav > div.side-menu > div.side-menu-header > button.text.menu-btn > span',
        on: onlyVerticalAlignment ? 'bottom' : 'left',
      },
      beforeShowPromise: () =>
        _preparations({
          shouldOpenSideNav: true,
          shouldOpenCardsMenu: false,
        }),
      buttons: [buttons.back, buttons.next],
      highlightClass: 'hover-effect-btn',
      classes: 'step-apps',
      id: 'appsSelector',
      title: t('onboarding.shared.productSuiteTitle'),
      text: t('onboarding.shared.productSuiteText'),
    } as shepherd.Step.StepOptions,
    {
      attachTo: {
        element: '.assistant-fab-button',
        on: onlyVerticalAlignment ? 'top' : 'right-end',
      },
      beforeShowPromise: () =>
        _preparations({ shouldOpenCardsMenu: false, shouldOpenSideNav: false }),
      buttons: [buttons.back, buttons.cancel],
      classes: 'step-assistant',
      id: 'assistant',
      title: t('onboarding.shared.assistantTitle'),
      text: t('onboarding.shared.assistantText'),
    } as shepherd.Step.StepOptions,
  ] as shepherd.Step.StepOptions[];
};

export const analyticsTour = (
  deviceType: TourDeviceType,
  translate: TranslateService
): ITour => {
  const t = (key: string) => translate.instant(key);
  return {
    welcomeMsg: t('onboarding.analytics.welcome'),
    tourDescription: t('onboarding.analytics.description'),
    path: 'analytics',
    icon: 'product_analytics',
    steps: analyticsSteps(deviceType, translate),
  };
};
